import { useState, useEffect } from "react";
import { Form } from 'react-bootstrap';
import "./SingleItemReturn.css";
import { useTheme } from "../../context/PortalThemeContext";
import { ReturnStates } from "./ReturnStates";
import { ActionCodeIcons, ActionCodeReturnState } from "./ActionCodeEnums";
import { ItemCard } from "../shared/ProductVariantCards";
import NavButtons from "./NavButtons";
import ExchangeForVariant from "./ExchangeForVariant";
import ModalOverlay from "../shared/ModalOverlay";
import AttachImageOption from "../shared/AttachImageOption";
import { fetchWithSessionToken } from "../shared/fetchWithSessionToken";
import { CHAT_API_BASE } from "../../config/api";
import ExchangeForProduct from "./ExchangeForProduct";

const MODAL_STEPS = {
    PRODUCT: 'EXCHANGE_PRODUCT',
    VARIANT: 'EXCHANGE_VARIANT',
};

const SingleItemReturn = ({
    selectedItem,
    selectedVariant,
    setSelectedVariant,
    setItemIdToReturnReasons,
    onStepChange,
    shopifyProducts,
    setSelectedExchangeProduct,
    selectedExchangeProduct,
    orderDetails,
}) => {
    function getState(toStateId) {
        return returnFlow?.states.find(state => state.stateId === toStateId) ?? null;
    }

    const [returnFlow, setReturnFlow] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [navStack, setNavStack] = useState([]);
    const currentState = getState(navStack[navStack.length - 1]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [storedReasons, setStoredReasons] = useState([]);
    const [loadingError, setLoadingError] = useState(null);
    const [error, setError] = useState(false);
    const [optionalText, setOptionalText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [actionStep, setActionStep] = useState(null);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [modalStep, setModalStep] = useState(null);

    const theme = useTheme();

    useEffect(() => {
        // Only show variant exchange option if product has multiple variants or variant options
        const matchedProduct = shopifyProducts?.find((p) => p.id === selectedItem?.product_id);
        const hasVariantOptions = matchedProduct?.variantTitleToOptions &&
            Object.keys(matchedProduct.variantTitleToOptions).some(key => key !== 'Title' && matchedProduct.variantTitleToOptions[key].length > 1);
        const hasMultipleVariants = matchedProduct?.variants?.length > 1;

        setActionStep(prevStep => {
            const newStep = ActionCodeReturnState[getState(selectedReason?.toStateId)?.actionCode];
            // If the step would be EXCHANGE_VARIANT but product has no variant options, skip to confirmation
            if (newStep === ReturnStates.EXCHANGE_VARIANT && !hasVariantOptions && !hasMultipleVariants) {
                return ReturnStates.RETURN_CONFIRMATION;
            }
            return newStep;
        });
    }, [selectedReason, getState, selectedItem, shopifyProducts]);

    // Load return options
    useEffect(() => {
        const getReturnFlow = async () => {
            setIsLoading(true);
            setLoadingError(null);

            const shopId = theme.store;

            try {
                const url = new URL(`${CHAT_API_BASE}/workflow/${shopId}/return_flow`);

                const response = await fetchWithSessionToken(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    setLoadingError('No options available. Please try again later.');
                    setIsLoading(false);
                    return;
                }

                const returnFlowJson = await response.json();
                const returnFlow = {
                    shopId: returnFlowJson.shopId,
                    workflowId: returnFlowJson.workflowId,
                    name: returnFlowJson.name,
                    startState: returnFlowJson.startState,
                    states: (returnFlowJson.states || []).map((state) => ({
                        stateId: state.stateId,
                        name: state.name,
                        title: state.title,
                        type: state.type,
                        actionCode: state.actionCode,
                        transitions: (state.transitions || []).map((transition) => ({
                            name: transition.name,
                            toStateId: transition.toStateId,
                            showOptionalText: transition.showOptionalText,
                            allowImageUpload: transition.allowImageUpload,
                        })),
                        isFinal: state.isFinal
                    }))
                };

                setReturnFlow(returnFlow);
                setNavStack([returnFlow.startState]);
                setIsLoading(false);

            } catch (err) {
                console.error('returnFlow error:', err);
                setLoadingError('Something went wrong. Please try again.');
            }

            setIsLoading(false);
        };
        getReturnFlow();
    }, [selectedItem, theme.store]);

    // This should only happen on mockStep
    if (!selectedItem) {
        console.warn('SingleItemReturn rendered without a selected item');
        return null;
    }

    const handleNext = () => {
        if (!selectedItem) {
            console.warn('No item selected');
            return;
        }

        setItemIdToReturnReasons(prev => ({
            ...prev,
            [selectedItem.id]: [...(prev[selectedItem.id] || []), ...storedReasons],
        }));
        if (actionStep === ReturnStates.RETURN_CONFIRMATION) {
            onStepChange(ReturnStates.RETURN_CONFIRMATION);
        } else if (actionStep === ReturnStates.EXCHANGE_PRODUCT) {
            setShowModal(true);
            setModalStep('PRODUCT');
        } else if (actionStep === ReturnStates.EXCHANGE_VARIANT) {
            setShowModal(true);
            setModalStep('VARIANT');
        } else {
            setNavStack((prev) => [...prev, selectedReason.toStateId]);
            setStoredReasons((prev) => [...prev, selectedReason]);
            setSelectedReason(null);
        }
    };

    const handleBack = () => {
        if (navStack.length === 1) {
            onStepChange(ReturnStates.SELECT_RETURN_ITEMS);
        } else {
            setStoredReasons((prevState) => {
                if (prevState.length === 1) {
                    return []
                }
                const reason = prevState[prevState.length - 1];
                setUploadedImages(reason.images);
                setOptionalText(reason.optionalText);
                setSelectedReason(reason);
                return prevState.slice(0, -1)
            });
            setNavStack((prevState) => prevState.slice(0, -1));
        }
    }

    const matchedProduct = shopifyProducts?.find((p) => p.id === selectedItem?.product_id);
    // Calculate total order value for proportional discounts
    const subtotalLineItems = orderDetails?.line_items?.reduce((sum, item) => sum + parseFloat(item.price), 0) ?? 0;

    // Check if product has variant options
    const hasVariantOptions = matchedProduct?.variantTitleToOptions &&
        Object.keys(matchedProduct.variantTitleToOptions).some(key => key !== 'Title' && matchedProduct.variantTitleToOptions[key].length > 1);
    const hasMultipleVariants = matchedProduct?.variants?.length > 1;

    // Filter transitions to remove exchange variant option if product has no variants
    const getFilteredTransitions = (state) => {
        if (!state?.transitions) return [];
        return state.transitions.filter(transition => {
            const nextState = getState(transition.toStateId);
            // Remove EXCHANGE_VARIANT option if no variant options
            if (nextState?.actionCode === 'EXCHANGE_VARIANT' && !hasVariantOptions && !hasMultipleVariants) {
                return false;
            }
            return true;
        });
    };

    return (
        <>
            {/* Modal for exchange */}
            {showModal && (
                <ModalOverlay onClose={() => {
                    setModalStep(null);
                    setShowModal(false);
                }}>
                    {modalStep === 'PRODUCT' && (
                        <ExchangeForProduct
                            selectedItemToReturn={selectedItem}
                            handleCancel={() => {
                                setSelectedExchangeProduct(null);
                                setModalStep(null);
                                setShowModal(false);
                            }}
                            shopifyProducts={shopifyProducts}
                            onNext={() => { setModalStep('VARIANT'); }}
                            onSelectProduct={(product) => {
                                setSelectedExchangeProduct(product);
                            }}
                        />
                    )}

                    {modalStep === 'VARIANT' && (
                        <ExchangeForVariant
                            selectedItemToReturn={selectedItem}
                            selectedVariant={selectedVariant}
                            setSelectedVariant={setSelectedVariant}
                            exchangeProduct={selectedExchangeProduct}
                            orderDiscount={orderDetails?.current_total_discounts}
                            orderTotal={subtotalLineItems}
                            handleCancel={(isBack) => {
                                if (isBack && selectedExchangeProduct) {
                                    setModalStep('PRODUCT');
                                } else {
                                    setModalStep(null);
                                    setShowModal(false);
                                }
                            }}
                            onStepChange={(step) => {
                                if (step === ReturnStates.RETURN_CONFIRMATION) {
                                    setShowModal(false);
                                    setModalStep(null);
                                    onStepChange(ReturnStates.RETURN_CONFIRMATION);
                                }
                            }}
                            shopifyProducts={shopifyProducts}
                            cameFromModal={true}
                        />
                    )}
                </ModalOverlay>
            )}

            <div className="d-flex-column flex-md-row align-items-top"
                style={{ gap: '4rem', padding: '1rem' }}>
                {/* Item Card */}
                <div className="d-flex-column w-100 align-items-center" style={{ flex: 2 }} >
                    <ItemCard
                        item={selectedItem}
                        shopifyProduct={matchedProduct}
                        orderDiscount={orderDetails?.current_total_discounts}
                        orderTotal={subtotalLineItems}
                        flowType="RETURN"
                    />

                    {storedReasons.length > 0 && (
                        <div style={{
                            fontSize: '1.25rem',
                            fontWeight: 400,
                            color: theme.textDark,
                            marginTop: '1.125rem'
                        }}>
                            <span>Return reason: </span>
                            {storedReasons.map((reason, index) => (
                                <span key={reason + index} style={{ fontWeight: 600 }}>
                                    {reason.name + (reason.optionalText ? " - " + reason.optionalText : "")}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Title and Options List */}
                <div style={{ flex: 3 }}>
                    {loadingError && (
                        <h3>{loadingError}</h3>
                    )}

                    {isLoading && (
                        <div className="spinner" />
                    )}

                    {currentState && !loadingError && (
                        <>
                            <h1 className="title">{currentState.title}</h1>
                            {/* Radio buttons for issue selection */}
                            <Form className="my-radio-group" style={{ marginTop: '2.25rem' }}>
                                {getFilteredTransitions(currentState)
                                    .map((t, index) => {
                                        const id = `option${index + 1}`;
                                        const isSelected = selectedReason?.name === t.name;
                                        const transition = {
                                            ...t,
                                            optionalText: isSelected ? selectedReason.optionalText : '',
                                            images: isSelected ? selectedReason.images : [],
                                        }
                                        const icon = ActionCodeIcons[getState(transition.toStateId)?.actionCode];

                                        return (
                                            <div key={id} style={{ width: '100%' }}>
                                                <label
                                                    htmlFor={id}
                                                    className={`form-check full-width-label ${isSelected ? 'selected' : ''}${icon ? ' action' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        type="radio"
                                                        id={id}
                                                        name="reason"
                                                        value={transition.name}
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            setError(false);
                                                            setSelectedReason(transition);
                                                        }}
                                                        className="form-check-input"
                                                        style={icon ? { display: 'none' } : {}}
                                                    />

                                                    {icon ? (
                                                        <img
                                                            src={icon}
                                                            alt={`${transition.name} icon`}
                                                            className='form-check-icon'
                                                        />
                                                    ) : null}

                                                    <span className={`form-check-label${icon ? ' action' : ''}`}>
                                                        {transition.name}
                                                    </span>
                                                </label>

                                                {/* Conditional input for optional text */}
                                                {isSelected && (transition.showOptionalText || transition.allowImageUpload) && (
                                                    <div style={{ paddingLeft: '1.125rem', marginTop: '0.75rem' }}>
                                                        {transition.showOptionalText && (
                                                            <textarea
                                                                type="text"
                                                                value={transition?.optionalText ?? ''}
                                                                onChange={(e) => {
                                                                    transition.optionalText = e.target.value;
                                                                    setSelectedReason(transition);
                                                                    setError(false)
                                                                }}
                                                                placeholder="Write in your return reason"
                                                                rows={3}
                                                                style={{ resize: 'vertical' }}
                                                                className="form-control mb-2"
                                                            />
                                                        )}

                                                        {transition.allowImageUpload && (
                                                            <AttachImageOption uploadedImages={transition.images} setUploadedImages={(uploads) => setSelectedReason({ ...transition, images: uploads })} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </Form>
                        </>
                    )}
                </div>
            </div>

            <div className="sticky-bottom" style={{ marginTop: '2rem', zIndex: '100' }}>
                {error && (
                    <p className="no-selection-error">Please select at least one option to proceed.</p>
                )}
                <NavButtons handleBack={handleBack} handleNext={handleNext} disabled={!selectedReason} onDisabledClick={() => setError(true)} />
            </div>

        </>
    );
};

export default SingleItemReturn;
